import React from 'react';
import { Users, BookOpen, Award } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Aboutus = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            {/* Hero Section */}
            <section className="pt-32 pb-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h1 className="text-sm uppercase tracking-widest text-gray-500 font-medium mb-4">About Us</h1>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            About <span className="text-blue-600">Wenergie</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            We're building a next-generation electricity company that's making power
                            clean, reliable, and rewarding for everyone.
                        </p>
                        <p className="text-xl font-semibold text-orange-500 mt-6">
                            Locally Powered. Globally Connected. Future Ready.
                        </p>
                    </div>
                </div>
            </section>

            {/* Who We Are Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        {/* Who We Are Header */}
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
                                <Users className="h-10 w-10 text-blue-600" />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                Who We Are
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                We are creators, makers at heart. Our team comprises of some of the most talented engineers
                                and designers, working with a passion to deliver resilient, clean and decentralised electric grid.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Story Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        {/* Our Story Header */}
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                                <BookOpen className="h-10 w-10 text-green-600" />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                Our Story
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Founded in 2020, Wenergie began with a simple idea: to revolutionize how people consume
                                and relate to energy in their daily lives.
                            </p>
                        </div>

                        {/* Mission & Vision Cards */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                            {/* Mission Card */}
                            <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-8">
                                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                                    Our Mission
                                </h3>
                                <p className="text-gray-700 mb-4">
                                    Our mission is to accelerate the transition to sustainable energy by creating innovative
                                    solutions that make renewable power accessible, affordable, and beneficial for everyone.
                                </p>
                                <p className="text-gray-700">
                                    We believe in a future where energy is produced locally, consumed efficiently, and shared
                                    within communities - creating a more resilient and sustainable energy ecosystem.
                                </p>
                            </div>

                            {/* Vision Card */}
                            <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-8">
                                <h3 className="text-2xl font-bold text-green-600 mb-4">
                                    Our Vision
                                </h3>
                                <p className="text-gray-700 mb-4">
                                    We envision a world where clean energy is the norm, not the exception. Where communities
                                    generate their own power from the sun, and smart technology optimizes energy use automatically.
                                </p>
                                <p className="text-gray-700">
                                    By building a network of interconnected homes and businesses, we're creating the energy grid of
                                    tomorrow - one that's decentralized, democratic, and 100% renewable.
                                </p>
                            </div>
                        </div>

                        {/* Conclusion */}
                        <div className="text-center">
                            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                                Wenergie is more than just an electricity provider. We're building a movement of people who want
                                to be part of the clean energy revolution - earning rewards while reducing their environmental
                                impact and contributing to a more sustainable future for all.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* News and Awards Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        {/* News Header */}
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
                                <Award className="h-10 w-10 text-purple-600" />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                News and Awards
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Our journey has been recognized by industry leaders and media. Here are some of our achievements.
                            </p>
                        </div>

                        {/* News Items */}
                        <div className="space-y-6">
                            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                <div className="flex flex-col md:flex-row">
                                    <div className="md:w-1/4 p-6 flex items-center justify-center bg-white">
                                        <img src="/images/social-alpha-logo.png" alt="Social Alpha" className="max-h-16" />
                                    </div>
                                    <div className="md:w-3/4 p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Wenergie Announced Among 17 Winners of Social Alpha's Techtonic- Innovation in Clean Energy Challenge</h3>
                                        <p className="text-sm text-gray-500 mb-4">Published by Social Alpha | July 06, 2021</p>
                                        <a href="https://www.socialalpha.org/announcement/social-alpha-announces-top-17-high-impact-cleantech-start-ups-of-techtonic-innovations-in-clean-energy-challenge/" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" target="_blank" rel="noopener noreferrer">Read</a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                <div className="flex flex-col md:flex-row">
                                    <div className="md:w-1/4 p-6 flex items-center justify-center bg-white">
                                        <img src="/images/eq-logo.png" alt="Amplus Solar" className="max-h-16" />
                                    </div>
                                    <div className="md:w-3/4 p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Wenergie Selected by Amplus Solar Into the MILES Challenge Green List</h3>
                                        <p className="text-sm text-gray-500 mb-4">Published in EQ International Magazine | March 18, 2021</p>
                                        <a href="https://www.eqmagpro.com/amplus-announces-the-miles-challenge-green-list/" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" target="_blank" rel="noopener noreferrer">Read</a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                <div className="flex flex-col md:flex-row">
                                    <div className="md:w-1/4 p-6 flex items-center justify-center bg-white">
                                        <img src="/images/saur-energy-logo.png" alt="Saur Energy" className="max-h-16" />
                                    </div>
                                    <div className="md:w-3/4 p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Amplus Solar Announces the Finalists of 'The MILES Challenge'</h3>
                                        <p className="text-sm text-gray-500 mb-4">Published in Saur Energy International | March 18, 2021</p>
                                        <a href="https://www.saurenergy.com/solar-energy-news/amplus-solar-announces-the-finalists-of-the-miles-challenge" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" target="_blank" rel="noopener noreferrer">Read</a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                <div className="flex flex-col md:flex-row">
                                    <div className="md:w-1/4 p-6 flex items-center justify-center bg-white">
                                        <img src="/images/siliconindia-logo.png" alt="SiliconIndia" className="max-h-16" />
                                    </div>
                                    <div className="md:w-3/4 p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Wenergie: Powering the Future Together</h3>
                                        <p className="text-sm text-gray-500 mb-4">Published in SiliconIndia Magazine | January 14, 2021</p>
                                        <a href="https://startup.siliconindia.com/vendor/wenergie-powering-the-future-together-cid-14241.html" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" target="_blank" rel="noopener noreferrer">Read</a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                <div className="flex flex-col md:flex-row">
                                    <div className="md:w-1/4 p-6 flex items-center justify-center bg-white">
                                        <img src="/images/millennial-dollar-logo.png" alt="Millennial Dollar Stories" className="max-h-16" />
                                    </div>
                                    <div className="md:w-3/4 p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">An Interview with Akshay Harikumar, Founder and CEO, Wenergie</h3>
                                        <p className="text-sm text-gray-500 mb-4">Published in Millennial Dollar Stories | August 11, 2020</p>
                                        <a href="https://millennialdollarstories.com/2020/08/11/akshay-harikumar-founder-wenergie/" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" target="_blank" rel="noopener noreferrer">Read</a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                <div className="flex flex-col md:flex-row">
                                    <div className="md:w-1/4 p-6 flex items-center justify-center bg-white">
                                        <img src="/images/economic-times-logo.png" alt="Economic Times" className="max-h-16" />
                                    </div>
                                    <div className="md:w-3/4 p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Bengaluru street vendors can soon tap solar energy</h3>
                                        <p className="text-sm text-gray-500 mb-4">Published in The Economic Times | July 27, 2017</p>
                                        <a href="https://economictimes.indiatimes.com/industry/energy/power/bengaluru-street-vendors-can-soon-tap-solar-energy/articleshow/59786403.cms" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" target="_blank" rel="noopener noreferrer">Read</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to join the clean energy revolution?</h2>
                        <p className="text-xl text-gray-600 mb-8">Join thousands of households already saving energy and earning rewards with Wenergie.</p>
                        <a
                            href="#"
                            className="inline-block px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-all duration-300 transform hover:scale-105"
                        >
                            Get Started Today
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Aboutus;